import { useState, useEffect, useMemo } from 'react';
import { Dropdown, Checkbox, Button, Typography, Spin } from 'antd';
import { LoadingOutlined, CheckOutlined } from '@ant-design/icons';
import { UploadCloudIcon, ExpandIcon } from 'src/icons';
import { useSelector, useDispatch } from 'react-redux';
import {
  useUpdatePractitionerSettings,
  useGetPractitionerSettings,
} from 'src/hooks/settings';
import { showToastSuccess } from 'src/utilities/toast';
import { useIsOP } from 'src/hooks/misc/useTenant';

import {
  listTemplateOverrides,
  listTemplates,
} from 'src/modules/ambient_solo/store/actions/settings';

const { Link } = Typography;

// We might need to get this list dynamically from the document structure in the future
// For now, these are the common sections as fallback
const ALL_POSSIBLE_SECTIONS = [
  'Chief Complaint',
  'History of Present Illness',
  'Review of Systems',
  'Physical Examination',
  'Assessment',
  'Plan',
  'Prescriptions',
  'Follow-up',
];

const EHRSubmissionDropdown = ({
  onSaveToEHR,
  isSubmitting,
  record,
  ehrSubmissionTooltip,
  practitionerSettings: practitionerSettingsProp,
  currentTemplate,
  isSmallScreen,
  className,
  encounterPhase = 'in-visit',
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const isOP = useIsOP();
  const { data: practitionerSettingsData } = useGetPractitionerSettings();
  const { mutateAsync: updateSettings } = useUpdatePractitionerSettings();

  const practitionerSettings =
    practitionerSettingsData || practitionerSettingsProp;

  const { templateList, templateOverridesList } = useSelector(
    (state) => state?.settingsState
  );

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  useEffect(() => {
    if (!templateList || templateList.length === 0) {
      dispatch(listTemplates(encounterPhase, currentPractitioner?.org_uuid));
    }
    if (!templateOverridesList || templateOverridesList.length === 0) {
      dispatch(listTemplateOverrides(encounterPhase));
    }
  }, [encounterPhase, dispatch, currentPractitioner?.org_uuid]);

  // Derive sections from template if available
  const availableSections = useMemo(() => {
    if (!isOP) return [];
    // Check if we have mapped sections in practitioner settings
    const templateId = record?.in_visit_template_id;
    const mappedSections =
      templateId &&
      practitionerSettings?.note_preferences?.op_ehr_push_mapping?.[templateId]
        ?.mapping?.sections;

    if (Array.isArray(mappedSections) && mappedSections.length > 0) {
      return mappedSections.map((s) => s.name);
    }

    // Fallback to template sections
    if (templateId) {
      const template =
        templateList?.find((t) => t.template_id === templateId) ||
        templateOverridesList?.find(
          (t) => t.template_overrides_id === templateId
        );
      if (template?.body?.sections) {
        return template.body.sections.map((s) => s.name);
      }
    }

    // Also check overrides ID
    const templateOverridesId = record?.in_visit_template_overrides_id;
    if (templateOverridesId) {
      const template = templateOverridesList?.find(
        (t) => t.template_overrides_id === templateOverridesId
      );
      if (template?.body?.sections) {
        return template.body.sections.map((s) => s.name);
      }
    }

    return ALL_POSSIBLE_SECTIONS;
  }, [record, practitionerSettings, isOP, templateList, templateOverridesList]);

  const initialSelectedSections = useMemo(() => {
    if (!isOP) return [];
    const templateId = record?.in_visit_template_id;
    const mappedSections =
      templateId &&
      practitionerSettings?.note_preferences?.op_ehr_push_mapping?.[templateId]
        ?.mapping?.sections;

    if (Array.isArray(mappedSections) && mappedSections.length > 0) {
      return mappedSections
        .filter((s) => s.allow_push !== false)
        .map((s) => s.name);
    }

    // Default to all available sections if no mapping
    return availableSections;
  }, [record, practitionerSettings, isOP, availableSections]);

  const [selectedSections, setSelectedSections] = useState(
    initialSelectedSections
  );
  const [_isDefaultSet, setIsDefaultSet] = useState(false);

  // Update selected sections when availableSections changes (e.g. template loads)
  useEffect(() => {
    setSelectedSections(initialSelectedSections);
  }, [initialSelectedSections]);

  const handleSectionToggle = (section) => {
    const newSelection = selectedSections.includes(section)
      ? selectedSections.filter((s) => s !== section)
      : [...selectedSections, section];
    setSelectedSections(newSelection);
  };

  const handleCheckAll = () => {
    setSelectedSections(availableSections);
  };

  const handleUncheckAll = () => {
    setSelectedSections([]);
  };

  const handleSetAsDefault = async () => {
    const templateId =
      record?.in_visit_template_id || currentTemplate?.template_id;
    if (!templateId) return;

    const existingMapping =
      practitionerSettings?.note_preferences?.op_ehr_push_mapping?.[templateId];

    let updatedSections;

    if (existingMapping?.mapping?.sections) {
      updatedSections = existingMapping.mapping.sections.map((section) => ({
        ...section,
        allow_push: selectedSections.includes(section.name),
      }));
    } else {
      updatedSections = availableSections.map((name) => ({
        name,
        allow_push: selectedSections.includes(name),
      }));
    }

    const payload = {
      op_ehr_push_mapping: {
        ...practitionerSettings?.note_preferences?.op_ehr_push_mapping,
        [templateId]: {
          ...(existingMapping || {}),
          mapping: {
            ...(existingMapping?.mapping || {}),
            sections: updatedSections,
          },
        },
      },
    };

    try {
      await updateSettings({ settings: payload });
      showToastSuccess(
        `Selections are saved for ${currentTemplate?.name || 'template'}`
      );
      setIsDefaultSet(true);
    } catch (error) {
      console.error('Failed to save default sections', error);
    }
  };

  const handleSelectiveSave = () => {
    if (!availableSections || availableSections.length === 0) {
      onSaveToEHR(null);
    } else {
      onSaveToEHR(selectedSections);
    }
  };

  const isAllSelected =
    availableSections.length > 0 &&
    selectedSections.length === availableSections.length;
  const isInitialSameAsCurrent =
    selectedSections.length === initialSelectedSections.length &&
    selectedSections.every((section) =>
      initialSelectedSections.includes(section)
    );
  const selectionCount = selectedSections.length;
  const hasChangesFromAll = !isInitialSameAsCurrent;

  const menuContent = (
    <div
      className="bg-white rounded-lg shadow-lg w-64 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex gap-2 mb-3">
        <Button
          onClick={handleCheckAll}
          className="flex-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700"
        >
          Check All
        </Button>
        <Button
          onClick={handleUncheckAll}
          className="flex-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700"
        >
          Uncheck All
        </Button>
      </div>

      <div className="max-h-60 overflow-y-auto mb-3">
        {availableSections.map((section) => (
          <div key={section} className="mb-2 flex items-center">
            <Checkbox
              checked={selectedSections.includes(section)}
              onChange={() => handleSectionToggle(section)}
            >
              {section}
            </Checkbox>
          </div>
        ))}
      </div>

      {hasChangesFromAll && (
        <div className="mb-3 text-center">
          <Link onClick={handleSetAsDefault} className="text-xs">
            Set as default
          </Link>
        </div>
      )}

      {/* <Divider className="my-2" /> */}

      {/* <Button
        type="primary"
        block
        onClick={handleSelectiveSave}
        disabled={isSubmitting || selectedSections.length === 0}
        className="bg-[#5667E9]"
      >
        {isSubmitting ? 'Saving...' : 'Save to EHR'}
      </Button> */}
    </div>
  );

  // Determine button label and icon based on state
  const isSuccess =
    record?.encounter_ehr_data?.document_submitted_at ||
    ehrSubmissionTooltip.status === 'success';

  const renderButtonContent = () => {
    if (isSmallScreen) {
      return (
        <div className="flex items-center cursor-pointer py-[1px] text-xs gap-1">
          {isSubmitting ? (
            <Spin
              indicator={
                <LoadingOutlined
                  style={{
                    fontSize: 16,
                    color: '#fff',
                  }}
                  spin
                />
              }
            />
          ) : isSuccess ? (
            <CheckOutlined style={{ color: '#fff' }} />
          ) : (
            <UploadCloudIcon
              height="17"
              width="17"
              className="hover:scale-110 transition-transform"
            />
          )}
        </div>
      );
    }

    // Normal Screen Content
    const actionText = isOP ? 'Save to OP' : 'Save to EHR';
    const submittedText = isOP ? 'Submitted to OP' : 'Submitted to EHR';

    let label = actionText;
    if (isSubmitting) {
      label = 'Saving...';
    } else if (isSuccess) {
      label = submittedText;
    } else if (!isOP) {
      label = actionText;
    } else if (isAllSelected) {
      label = `${actionText} (All)`;
    } else {
      label = `${actionText} (${selectionCount})`;
    }

    return (
      <div className="flex items-center gap-2">
        {isSubmitting ? (
          <Spin
            indicator={
              <LoadingOutlined style={{ fontSize: 16, color: '#fff' }} spin />
            }
          />
        ) : isSuccess ? (
          <CheckOutlined style={{ color: '#fff' }} />
        ) : (
          <div className="flex items-center">
            <UploadCloudIcon height="15" width="15" />
          </div>
        )}
        <span className="text-xs md:text-sm pt-0.5">{label}</span>
      </div>
    );
  };

  return availableSections && availableSections.length > 0 ? (
    <Dropdown
      overlay={menuContent}
      trigger={['click']}
      placement="bottomRight"
      arrow
      openClassName="ehr-dropdown-open"
      disabled={disabled}
    >
      <div
        className={`bg-[#5667E9] hover:bg-[#5667E9] flex items-center justify-center text-white font-normal rounded-xl focus:outline-none focus:shadow-outline cursor-pointer transition-colors h-[35px] md:h-[36px] ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {/* Split button styling */}
        <Button
          type="text"
          className="text-white hover:text-white hover:bg-transparent border-0 flex items-center px-2 md:px-3 h-full"
          disabled={isSubmitting || disabled}
          onClick={(e) => {
            e.stopPropagation();
            handleSelectiveSave();
          }}
          style={{
            borderRight: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '0.75rem 0 0 0.75rem',
          }}
        >
          {renderButtonContent()}
        </Button>

        <div
          className="px-1.5 md:px-2 flex items-center justify-center h-full hover:bg-[#4a5bd9] rounded-r-xl"
          style={{ borderRadius: '0 0.75rem 0.75rem 0' }}
        >
          <div className="mb-0.5 mt-1.5">
            <ExpandIcon fill="#ffffff" />
          </div>
        </div>
      </div>
    </Dropdown>
  ) : (
    <Button
      type={'button'}
      className={`bg-[#5667E9] hover:bg-[#5667E9] flex items-center justify-center text-white font-normal p-2 px-4 rounded-xl focus:outline-none focus:shadow-outline h-[35px] md:h-[36px] ${className}`}
      onClick={handleSelectiveSave}
      disabled={isSubmitting || disabled}
    >
      {renderButtonContent()}
    </Button>
  );
};

export default EHRSubmissionDropdown;
